import { useState, useEffect } from 'react';
import { GOLD, MUTE, LIGHT, MONO } from '../lib/constants';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PayerCodeLinker
 *
 * Shown on the Payments tab when unlinked payment codes are detected.
 * Lets the user confirm which code belongs to which debt for precise matching.
 *
 * Props:
 *   debt        — the debt object (needs debt.id and debt.name)
 *   onCodeLinked — callback() called after a code is successfully linked
 */
export default function PayerCodeLinker({ debt, onCodeLinked }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(null);

  useEffect(() => {
    fetchUnlinkedCodes();
  }, []);

  const fetchUnlinkedCodes = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/debts/unlinked-payment-codes`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      setSuggestions(data.unlinked_codes || []);
    } catch (e) {
      console.error('Error fetching unlinked codes:', e);
    } finally {
      setLoading(false);
    }
  };

  const linkCode = async (code) => {
    try {
      setLinking(code);
      const res = await fetch(`${API}/api/debts/${debt.id}/payer-reference-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ payer_reference_code: code }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Could not link code');
        return;
      }
      onCodeLinked && onCodeLinked();
      setSuggestions(prev => prev.filter(s => s.code !== code));
    } catch (e) {
      console.error('Error linking code:', e);
    } finally {
      setLinking(null);
    }
  };

  const dismiss = (code) => setSuggestions(prev => prev.filter(s => s.code !== code));

  if (loading || suggestions.length === 0) return null;

  return (
    <div style={{
      marginBottom: 20,
      padding: 16,
      background: `${GOLD}08`,
      borderRadius: 12,
      border: `1px solid ${GOLD}30`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth="2">
          <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
        </svg>
        <span style={{ fontFamily: MONO, fontSize: 11, color: GOLD, letterSpacing: 1, fontWeight: 700 }}>
          UNMATCHED PAYMENTS DETECTED
        </span>
      </div>

      <p style={{ fontFamily: MONO, fontSize: 11, color: MUTE, margin: '0 0 14px', lineHeight: 1.6 }}>
        We found payment transactions we couldn't automatically link to a debt.
        Is one of these a payment for <strong style={{ color: LIGHT }}>{debt.name}</strong>?
      </p>

      {suggestions.map(s => (
        <div key={s.code} style={{
          background: 'var(--faint)',
          borderRadius: 10,
          padding: '12px 14px',
          marginBottom: 10,
          border: '1px solid var(--ghost)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
            <div>
              <span style={{ fontFamily: MONO, fontSize: 12, color: LIGHT, fontWeight: 700 }}>
                {s.issuer_hint || 'Unknown Issuer'}
              </span>
              <span style={{
                marginLeft: 8, fontFamily: MONO, fontSize: 10, color: MUTE,
                background: 'var(--base)', borderRadius: 4, padding: '2px 6px',
              }}>
                code: {s.code}
              </span>
            </div>
            <span style={{ fontFamily: MONO, fontSize: 10, color: MUTE }}>
              {s.transaction_count} payment{s.transaction_count !== 1 ? 's' : ''} · ${s.total_paid.toFixed(2)}
            </span>
          </div>

          {s.sample_transactions.slice(0, 2).map((txn, i) => (
            <div key={i} style={{ fontFamily: MONO, fontSize: 10, color: 'var(--ghost)', marginBottom: 2 }}>
              {txn.date} — ${txn.amount.toFixed(2)} — {txn.description?.slice(0, 40)}{txn.description?.length > 40 ? '…' : ''}
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button
              onClick={() => linkCode(s.code)}
              disabled={linking === s.code}
              data-testid={`link-code-${s.code}`}
              style={{
                flex: 1,
                padding: '9px 0',
                background: linking === s.code ? `${GOLD}60` : GOLD,
                color: '#000',
                border: 'none',
                borderRadius: 8,
                fontFamily: MONO,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: 0.5,
                cursor: linking === s.code ? 'not-allowed' : 'pointer',
                transition: 'opacity 0.15s',
              }}
            >
              {linking === s.code ? 'Linking...' : `Yes — this is ${debt.name}`}
            </button>
            <button
              onClick={() => dismiss(s.code)}
              data-testid={`dismiss-code-${s.code}`}
              style={{
                padding: '9px 14px',
                background: 'transparent',
                color: MUTE,
                border: '1px solid var(--ghost)',
                borderRadius: 8,
                fontFamily: MONO,
                fontSize: 11,
                cursor: 'pointer',
              }}
            >
              Not this card
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
